import { QuestionTypeV1 } from 'enum/question-type'
import type { IQuestionData } from 'models/question.model'
import type {
  SurveyTemplate,
  SurveyTemplateDraft,
  SurveyTemplateLabel,
  SurveyTemplatePayload,
  SurveyTemplateQuestion,
  SurveyTemplateQuestionType,
} from './types'

/** Draft question with a local key, so questions not saved yet can be edited. */
export interface DraftQuestion extends SurveyTemplateQuestion {
  key: number
}

const TYPE_BY_QUESTIONABLE: Record<string, SurveyTemplateQuestionType> = {
  [QuestionTypeV1.likert]: 'likert',
  [QuestionTypeV1.openEnded]: 'open_ended',
}

const QUESTIONABLE_BY_TYPE: Record<SurveyTemplateQuestionType, string> = {
  likert: QuestionTypeV1.likert,
  open_ended: QuestionTypeV1.openEnded,
}

interface EditorLabel {
  id?: number
  label?: string
  order?: number
  _destroy?: boolean
}

interface EditorSaveData {
  likert?: { likertLabelsAttributes: EditorLabel[] }
  question: { name: string; mandatory?: boolean }
}

const keptLabels = (labels: EditorLabel[]): SurveyTemplateLabel[] =>
  labels
    .filter(({ label, _destroy }) => !_destroy && !!label?.trim())
    .map(({ id, label }, index) => ({
      ...(id ? { id } : {}),
      label: label as string,
      order: index + 1,
    }))

/**
 * Turns what the shared question editor emits on save into a question of the
 * bank draft. Removed or emptied options are dropped: the bank endpoint
 * deletes the options that are missing from the payload.
 */
export const fromEditorSave = (
  data: EditorSaveData,
  questionableType: string,
  current?: DraftQuestion
): Omit<DraftQuestion, 'key'> => {
  const type = TYPE_BY_QUESTIONABLE[questionableType]
  return {
    ...(current?.id ? { id: current.id } : {}),
    type,
    name: data.question.name,
    mandatory: !!data.question.mandatory,
    labels:
      type === 'likert'
        ? keptLabels(data.likert?.likertLabelsAttributes ?? [])
        : [],
  }
}

/** Shape the shared question editor and card expect for a draft question. */
export const toEditorQuestion = (
  question: DraftQuestion,
  index: number
): IQuestionData =>
  ({
    id: question.key,
    instanceId: 0,
    questionableId: question.key,
    name: question.name,
    explanationAnswer: null,
    description: null,
    order: index + 1,
    mandatory: question.mandatory,
    type: question.type === 'likert' ? 'Likert' : 'Open Ended',
    questionableType: QUESTIONABLE_BY_TYPE[question.type],
    points: 0,
    url: '',
    urlType: 0,
    options: question.labels.map(({ id, label, order }) => ({
      id,
      label,
      order,
    })),
  } as unknown as IQuestionData)

/** Bank detail → editable draft, giving each question a stable local key. */
export const fromSurveyTemplate = (
  template: SurveyTemplate
): {
  draft: SurveyTemplateDraft
  questions: DraftQuestion[]
} => ({
  draft: {
    id: template.id,
    name: template.name,
    description: template.description ?? '',
    questions: template.questions,
  },
  questions: [...template.questions]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((question, index) => ({ ...question, key: index + 1 })),
})

/** Draft → body of the bank create/update endpoints, with orders renumbered. */
export const toSurveyTemplatePayload = (
  draft: SurveyTemplateDraft,
  questions: DraftQuestion[]
): SurveyTemplatePayload => ({
  surveyTemplate: {
    name: draft.name.trim(),
    description: draft.description,
    questions: questions.map(
      ({ id, type, name, mandatory, labels }, index) => ({
        ...(id ? { id } : {}),
        type,
        name,
        mandatory,
        order: index + 1,
        labels: type === 'likert' ? keptLabels(labels) : [],
      })
    ),
  },
})
